import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft } from "lucide-react";
import { getSafeErrorMessage } from "@/lib/errorUtils";
import { format } from "date-fns";

interface Submission {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  vehicle: string;
  service: string;
  message: string | null;
  status: string;
  created_at: string;
}

const Submissions = () => {
  const { isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [filter, setFilter] = useState<string>("all");

  useEffect(() => {
    if (!loading && !isAdmin) {
      navigate("/auth");
    }
    if (isAdmin) {
      fetchSubmissions();
    }
  }, [isAdmin, loading, navigate]);

  const fetchSubmissions = async () => {
    const { data, error } = await supabase
      .from("contact_submissions")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      toast({
        title: "Error loading submissions",
        description: getSafeErrorMessage(error),
        variant: "destructive",
      });
    } else {
      setSubmissions(data || []);
    }
  };

  const updateStatus = async (id: string, status: string) => {
    const { error } = await supabase
      .from("contact_submissions")
      .update({ status })
      .eq("id", id);

    if (error) {
      toast({
        title: "Error updating status",
        description: getSafeErrorMessage(error),
        variant: "destructive",
      });
    } else {
      toast({ title: "Status updated successfully!" });
      fetchSubmissions();
    }
  };

  const filteredSubmissions =
    filter === "all"
      ? submissions
      : submissions.filter((s) => s.status === filter);

  if (loading || !isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-darker-bg to-background">
      <div className="container mx-auto px-4 py-12">
        <div className="flex items-center gap-4 mb-8">
          <Button asChild variant="outline">
            <Link to="/admin">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Link>
          </Button>
          <h1 className="text-4xl font-heading font-bold">Contact Submissions</h1>
        </div>

        {/* Filter Buttons */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={filter === "all" ? "premium" : "outline"}
            onClick={() => setFilter("all")}
          >
            All ({submissions.length})
          </Button>
          <Button
            variant={filter === "new" ? "premium" : "outline"}
            onClick={() => setFilter("new")}
          >
            New ({submissions.filter((s) => s.status === "new").length})
          </Button>
          <Button
            variant={filter === "contacted" ? "premium" : "outline"}
            onClick={() => setFilter("contacted")}
          >
            Contacted ({submissions.filter((s) => s.status === "contacted").length})
          </Button>
          <Button
            variant={filter === "completed" ? "premium" : "outline"}
            onClick={() => setFilter("completed")}
          >
            Completed ({submissions.filter((s) => s.status === "completed").length})
          </Button>
        </div>

        {/* Submissions List */}
        <div className="space-y-4">
          {filteredSubmissions.length === 0 ? (
            <Card className="bg-card border-border">
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">No submissions found</p>
              </CardContent>
            </Card>
          ) : (
            filteredSubmissions.map((submission) => (
              <Card key={submission.id} className="bg-card border-border">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle>
                        {submission.first_name} {submission.last_name}
                      </CardTitle>
                      <p className="text-sm text-muted-foreground">
                        {format(new Date(submission.created_at), "PPpp")}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant={submission.status === "new" ? "premium" : "outline"}
                        onClick={() => updateStatus(submission.id, "new")}
                      >
                        New
                      </Button>
                      <Button
                        size="sm"
                        variant={submission.status === "contacted" ? "premium" : "outline"}
                        onClick={() => updateStatus(submission.id, "contacted")}
                      >
                        Contacted
                      </Button>
                      <Button
                        size="sm"
                        variant={submission.status === "completed" ? "premium" : "outline"}
                        onClick={() => updateStatus(submission.id, "completed")}
                      >
                        Completed
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Email</p>
                      <a
                        href={`mailto:${submission.email}`}
                        className="text-accent hover:underline"
                      >
                        {submission.email}
                      </a>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Phone</p>
                      <a
                        href={`tel:${submission.phone}`}
                        className="text-accent hover:underline"
                      >
                        {submission.phone}
                      </a>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Vehicle</p>
                      <p>{submission.vehicle}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Service</p>
                      <p className="capitalize">{submission.service}</p>
                    </div>
                  </div>
                  {submission.message && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        Message
                      </p>
                      <p className="text-sm bg-background p-3 rounded-md">
                        {submission.message}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Submissions;
